import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export interface PageBackTarget {
  /** A route to navigate to, or a handler to run instead. */
  to: string | (() => void);
  label: string;
}

interface PageBackValue {
  back: PageBackTarget | null;
  setBack: (t: PageBackTarget | null) => void;
}

const PageBackContext = createContext<PageBackValue>({ back: null, setBack: () => {} });

export function PageBackProvider({ children }: { children: ReactNode }) {
  const [back, setBack] = useState<PageBackTarget | null>(null);
  const value = useMemo(() => ({ back, setBack }), [back]);
  return <PageBackContext.Provider value={value}>{children}</PageBackContext.Provider>;
}

/** Read by TopHeaderBar, which owns the one place a page-level Back button renders. */
export function usePageBackTarget(): PageBackTarget | null {
  return useContext(PageBackContext).back;
}

/**
 * Registers this page's Back button in the top bar. Call it once at the top of a
 * page component; it clears itself when the page unmounts.
 *
 * This is for leaving the page. A Back that moves between views *inside* one page
 * (a wizard step, a drill-down, a detail panel) stays inline next to the content
 * it applies to -- putting that in the top bar would read as leaving the page.
 */
export function usePageBack(to: string | (() => void), label = "Back"): void {
  const { setBack } = useContext(PageBackContext);

  // A function target would be a new reference every render, so it is held in a
  // ref-like callback identity instead of being compared directly.
  const stable = useCallback(() => { if (typeof to === "function") to(); }, [to]);
  const isFn = typeof to === "function";
  const route = isFn ? null : to;

  useEffect(() => {
    setBack({ to: isFn ? stable : route!, label });
    return () => setBack(null);
    // `stable` is intentionally out of the deps: a page passing an inline arrow
    // would otherwise re-register on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBack, isFn, route, label]);
}
